import { EnvelopeSimple, LockKey, SignIn } from "@phosphor-icons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import type { MailSession } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, ApiError } from "../lib/api";

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cached = queryClient.getQueryData<MailSession>(["mail", "session"]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", window.matchMedia("(prefers-color-scheme: dark)").matches);
  }, []);
  const login = useMutation({
    mutationFn: () => api<MailSession & { csrfToken: string }>("/auth/mail/login", { method: "POST", body: JSON.stringify({ email, password }) }),
    onSuccess: (data) => {
      queryClient.setQueryData(["mail", "session"], data);
      navigate("/", { replace: true });
    }
  });
  if (cached) return <Navigate to="/" replace />;
  return (
    <main className="grid min-h-[100dvh] bg-background lg:grid-cols-[minmax(420px,46%)_1fr]">
      <section className="flex items-center border-r border-border bg-card px-7 py-14 sm:px-14 lg:px-[9vw]">
        <div className="w-full max-w-[390px]">
          <div className="mb-10"><img src={`${import.meta.env.BASE_URL}ascdnd-logo.png`} alt="ascdnd" className="h-auto w-[176px]" /></div>
          <h1 className="text-[32px] font-[660] tracking-[-.045em]">Sign in to your mailbox</h1>
          <p className="mt-2 text-sm text-muted-foreground">Use your assigned mail account.</p>
          <form className="mt-8 space-y-5" onSubmit={(event) => { event.preventDefault(); login.mutate(); }}>
            {login.error ? <div className="rounded-md border border-destructive/25 bg-destructive/8 px-3 py-2 text-sm text-destructive">{login.error instanceof ApiError ? login.error.message : "Sign-in failed"}</div> : null}
            <label className="block space-y-2"><span className="text-sm font-[590]">Email</span><div className="relative"><EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} /><Input className="pl-10" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" placeholder="name@company.com" required /></div></label>
            <label className="block space-y-2"><span className="text-sm font-[590]">Password</span><div className="relative"><LockKey className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} /><Input className="pl-10" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required /></div></label>
            <Button className="w-full" type="submit" disabled={login.isPending}>{login.isPending ? "Signing in" : <><SignIn size={18} />Sign in</>}</Button>
          </form>
        </div>
      </section>
      <aside className="relative hidden overflow-hidden bg-[#182642] p-14 text-[#f1f4fb] lg:flex lg:items-end">
        <div className="absolute inset-x-14 top-20 h-px bg-[#9bb0e8]/25" />
        <div className="absolute inset-x-14 top-[calc(5rem+96px)] h-px bg-[#9bb0e8]/14" />
        <div className="relative max-w-xl"><p className="text-[clamp(38px,5vw,68px)] font-[610] leading-[.98] tracking-[-.06em]">ASC DND Mail</p><p className="mt-6 text-base leading-7 text-[#c5d0e8]">Mail system</p></div>
      </aside>
    </main>
  );
}
